if (!customElements.get('zelink-video-carousel')) {
  class ZelinkVideoCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-zvc-track]');
      this.cards = Array.from(this.querySelectorAll('[data-zvc-card]'));
      if (!this.track || !this.cards.length) return;

      this.controller = new AbortController();
      this.frame = 0;
      this.pointer = null;
      this.suppressClickUntil = 0;
      const initial = Number(this.dataset.initialIndex);
      this.currentIndex = Number.isInteger(initial) && initial >= 0 && initial < this.cards.length ? initial : 0;
      this.muted = typeof this.muted === 'boolean' ? this.muted : true;
      this.previous = this.querySelector('[data-zvc-prev]');
      this.next = this.querySelector('[data-zvc-next]');
      this.status = this.querySelector('[data-zvc-status]');
      this.caption = this.querySelector('[data-zvc-caption]');
      this.controls = this.querySelector('[data-zvc-controls]');
      this.muteButton = this.querySelector('[data-zvc-mute]');
      this.soundOn = this.querySelector('[data-zvc-sound-on]');
      this.soundOff = this.querySelector('[data-zvc-sound-off]');
      this.videos = Array.from(this.querySelectorAll('video.zvc-video'));
      this.videoStates = new Map();
      const signal = this.controller.signal;

      this.videos.forEach((video) => {
        const card = video.closest('[data-zvc-card]');
        const button = card?.querySelector('[data-zvc-play]');
        const selector = card?.querySelector('[data-zvc-select]');
        const state = { card, button, selector, completed: false, tabIndex: video.getAttribute('tabindex') };
        this.videoStates.set(video, state);
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.pause();
        video.muted = this.muted;

        button?.addEventListener('click', () => {
          if (Date.now() < this.suppressClickUntil || !this.isActive(video)) return;
          this.preparePlayback(video);
          try {
            const playback = video.play();
            if (playback && typeof playback.catch === 'function') {
              playback.catch(() => this.syncPlayback(video));
            }
          } catch {
            this.syncPlayback(video);
          }
        }, { signal });
        selector?.addEventListener('click', () => {
          if (Date.now() < this.suppressClickUntil) return;
          this.select(this.cards.indexOf(card));
        }, { signal });
        video.addEventListener('loadedmetadata', () => this.initializeTrim(video), { signal });
        video.addEventListener('play', () => {
          this.preparePlayback(video);
          if (document.hidden || !this.isActive(video)) video.pause();
          this.syncPlayback(video);
        }, { signal });
        video.addEventListener('playing', () => this.syncPlayback(video), { signal });
        video.addEventListener('pause', () => this.syncPlayback(video), { signal });
        video.addEventListener('ended', () => {
          state.completed = true;
          this.syncPlayback(video);
        }, { signal });
        video.addEventListener('timeupdate', () => this.enforceTrim(video), { signal });
        video.addEventListener('seeking', () => this.enforceTrim(video), { signal });
        video.addEventListener('error', () => this.syncPlayback(video), { signal });
        video.addEventListener('volumechange', () => {
          if (!this.isActive(video)) return;
          this.muted = video.muted;
          this.syncMute();
        }, { signal });
        if (video.readyState >= 1) this.initializeTrim(video);
      });

      document.addEventListener('play', (event) => {
        const playing = event.target;
        if (!(playing instanceof HTMLVideoElement)) return;
        document.querySelectorAll('video').forEach((video) => {
          if (video !== playing && !video.paused) video.pause();
        });
      }, { capture: true, signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.videos.forEach((video) => video.pause());
      }, { signal });

      this.previous?.addEventListener('click', () => this.move(-1), { signal });
      this.next?.addEventListener('click', () => this.move(1), { signal });
      this.muteButton?.addEventListener('click', () => {
        this.muted = !this.muted;
        const active = this.activeVideo();
        if (active) active.muted = this.muted;
        this.syncMute();
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('video, input, textarea, select, [contenteditable="true"]')) return;
        const actions = { ArrowLeft: () => this.move(-1), ArrowRight: () => this.move(1), Home: () => this.select(0), End: () => this.select(this.cards.length - 1) };
        if (!actions[event.key]) return;
        event.preventDefault();
        actions[event.key]();
      }, { signal });
      this.track.addEventListener('pointerdown', (event) => {
        const video = event.target.closest('video');
        if (event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0) || (video && !video.paused)) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, { passive: true, signal });
      window.addEventListener('pointerup', (event) => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        const x = event.clientX - this.pointer.x;
        const y = event.clientY - this.pointer.y;
        this.pointer = null;
        if (Math.abs(x) < 40 || Math.abs(x) <= Math.abs(y) * 1.25) return;
        this.suppressClickUntil = Date.now() + 400;
        this.move(x < 0 ? 1 : -1);
      }, { passive: true, signal });
      window.addEventListener('pointercancel', () => { this.pointer = null; }, { passive: true, signal });
      window.addEventListener('resize', () => this.scheduleUpdate(), { passive: true, signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(this.track);
      }
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
            if ((!isIntersecting || intersectionRatio < 0.15) && !target.paused && this.visibleFraction(target) < 0.15) target.pause();
          });
        }, { threshold: [0, 0.15] });
        this.videos.forEach((video) => this.visibilityObserver.observe(video));
      } else {
        window.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      }

      this.classList.add('zvc-ready');
      if (this.controls) this.controls.hidden = false;
      this.update();
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.resizeObserver = null;
      this.visibilityObserver = null;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.pointer = null;
      this.videos.forEach((video) => {
        video.pause();
        video.controls = true;
        video.removeAttribute('aria-hidden');
        const state = this.videoStates.get(video);
        if (state.tabIndex === null) video.removeAttribute('tabindex');
        else video.setAttribute('tabindex', state.tabIndex);
        if (state.button) state.button.hidden = true;
        if (state.selector) state.selector.hidden = true;
      });
      this.cards.forEach((card) => {
        card.hidden = false;
        card.removeAttribute('data-position');
      });
      this.classList.remove('zvc-ready');
      if (this.controls) this.controls.hidden = true;
      this.videoStates.clear();
    }

    activeVideo() {
      return this.cards[this.currentIndex]?.querySelector('video.zvc-video') || null;
    }

    isActive(video) {
      return this.videoStates.get(video)?.card === this.cards[this.currentIndex];
    }

    bounds(video) {
      const read = (value) => {
        if (value === undefined || value.trim() === '') return null;
        const number = Number(value);
        return Number.isFinite(number) && number >= 0 ? number : null;
      };
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : Infinity;
      const requestedStart = read(video.dataset.start);
      const start = requestedStart !== null && requestedStart < duration ? requestedStart : 0;
      const requestedEnd = read(video.dataset.end);
      const end = requestedEnd !== null && requestedEnd > start ? Math.min(requestedEnd, duration) : null;
      return { start, end, hasStart: requestedStart !== null && requestedStart < duration };
    }

    seek(video, seconds) {
      if (video.readyState < 1 || Math.abs(video.currentTime - seconds) < 0.01) return;
      try {
        video.currentTime = seconds;
      } catch {}
    }

    initializeTrim(video) {
      const { start, hasStart } = this.bounds(video);
      if (hasStart) this.seek(video, start);
    }

    preparePlayback(video) {
      const state = this.videoStates.get(video);
      if (!state) return;
      const { start, end, hasStart } = this.bounds(video);
      if (state.completed || video.ended || (end !== null && video.currentTime >= end) || (hasStart && video.currentTime < start)) {
        state.completed = false;
        this.seek(video, start);
      }
    }

    enforceTrim(video) {
      const state = this.videoStates.get(video);
      if (!state) return;
      const { start, end, hasStart } = this.bounds(video);
      if (hasStart && video.currentTime < start) this.seek(video, start);
      if (end !== null && video.currentTime >= end) {
        state.completed = true;
        video.pause();
        this.syncPlayback(video);
      } else if (state.completed && !video.ended) {
        state.completed = false;
      }
    }

    syncPlayback(video) {
      const state = this.videoStates.get(video);
      if (!state) return;
      const active = this.isActive(video);
      const playing = active && !video.paused && !video.ended;
      video.controls = playing;
      video.tabIndex = playing ? 0 : -1;
      video.setAttribute('aria-hidden', String(!active));
      if (state.button) {
        state.button.hidden = !active || playing;
        if (playing && document.activeElement === state.button) video.focus({ preventScroll: true });
        else if (active && !playing && document.activeElement === video) state.button.focus({ preventScroll: true });
      }
    }

    syncMute() {
      if (this.muteButton) {
        const label = this.muted ? this.muteButton.dataset.unmuteLabel : this.muteButton.dataset.muteLabel;
        if (label) this.muteButton.setAttribute('aria-label', label);
        this.muteButton.disabled = !this.activeVideo();
      }
      if (this.soundOn) this.soundOn.hidden = this.muted;
      if (this.soundOff) this.soundOff.hidden = !this.muted;
    }

    visibleFraction(video) {
      const rect = video.getBoundingClientRect();
      const track = this.track.getBoundingClientRect();
      const width = Math.max(0, Math.min(rect.right, track.right, window.innerWidth) - Math.max(rect.left, track.left, 0));
      const height = Math.max(0, Math.min(rect.bottom, track.bottom, window.innerHeight) - Math.max(rect.top, track.top, 0));
      return rect.width > 0 && rect.height > 0 ? (width * height) / (rect.width * rect.height) : 0;
    }

    scheduleUpdate() {
      if (this.frame || !this.controller) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      if (!this.controller) return;
      const total = this.cards.length;
      this.cards.forEach((card, index) => {
        let position = (index - this.currentIndex + total) % total;
        if (position > total / 2) position -= total;
        card.dataset.position = String(position);
        card.hidden = Math.abs(position) > 2;
        const selector = card.querySelector('[data-zvc-select]');
        if (selector) selector.hidden = position === 0 || card.hidden;
      });
      this.videos.forEach((video) => {
        if (!this.isActive(video) || this.visibleFraction(video) < 0.15) video.pause();
        this.syncPlayback(video);
      });
      if (this.previous) this.previous.disabled = total < 2;
      if (this.next) this.next.disabled = total < 2;
      if (this.status) {
        const template = this.dataset.statusTemplate || '__CURRENT__ / __TOTAL__';
        const label = template.replaceAll('__CURRENT__', String(this.currentIndex + 1)).replaceAll('__TOTAL__', String(total));
        if (this.status.textContent !== label) this.status.textContent = label;
      }
      if (this.caption) {
        this.caption.textContent = this.cards[this.currentIndex].dataset.caption || '';
        this.caption.hidden = this.caption.textContent.trim() === '';
      }
      this.syncMute();
    }

    select(index) {
      if (!this.controller || !Number.isInteger(index)) return;
      const focused = document.activeElement;
      const focusWasOnMedia = Array.from(this.videoStates.entries()).some(([video, state]) => focused === video || focused === state.button || focused === state.selector);
      this.currentIndex = ((index % this.cards.length) + this.cards.length) % this.cards.length;
      const active = this.activeVideo();
      if (active) active.muted = this.muted;
      this.update();
      if (focusWasOnMedia) {
        const state = this.videoStates.get(active);
        if (state?.button && !state.button.hidden) state.button.focus({ preventScroll: true });
        else if (active) active.focus({ preventScroll: true });
      }
    }

    move(direction) {
      this.select(this.currentIndex + direction);
    }
  }

  customElements.define('zelink-video-carousel', ZelinkVideoCarousel);
}
